import type { Modifier, StatId } from '@gra/content';
import { nextRandom } from './rng';
import { applyModifiers } from './stats';
import type { GameState } from './types';

/**
 * Efekty specjalne z drzewka talentów i przedmiotów wyprawy (statystyki z EFFECT_STATS, bazowo 0):
 * dobijanie, obrażenia krytyczne, życie za zabójstwo, unik, bariera i kolce.
 */

/** Ile sekund bez ciosu, zanim bariera odnowi się w całości. */
export const BARRIER_DELAY = 6;
/** Co ile sekund kolce ranią dotykających wrogów. */
export const THORNS_TICK = 0.5;

// Lista modyfikatorów zmienia się tylko przy awansie i podniesieniu przedmiotu, więc wynik można trzymać.
const cache = new WeakMap<readonly Modifier[], Map<StatId, number>>();

/** Wartość efektu (suma modyfikatorów, bazowo 0). */
export function effect(s: GameState, stat: StatId): number {
  let byStat = cache.get(s.modifiers);
  if (!byStat) {
    byStat = new Map();
    cache.set(s.modifiers, byStat);
  }
  let v = byStat.get(stat);
  if (v === undefined) {
    v = applyModifiers(0, stat, s.modifiers);
    byStat.set(stat, v);
  }
  return v;
}

/** Mnożnik trafienia krytycznego. */
export const critMultiplier = (s: GameState) => 2 + effect(s, 'critDamage');

/**
 * Obrażenia dla bohatera po uniku i barierze. Unik działa tylko na pojedyncze ataki (pocisk, cios, wybuch),
 * nie na ciągły napór wrogów. Zwraca obrażenia, które przeszły przez osłony.
 */
export function shieldDamage(s: GameState, amount: number, dodgeable: boolean): number {
  const p = s.player;
  if (dodgeable && nextRandom(s) < Math.min(0.6, effect(s, 'dodgeChance'))) {
    s.events.push({ type: 'dodged', x: p.x, y: p.y });
    return 0;
  }
  p.shieldTimer = BARRIER_DELAY;
  if (p.shield > 0) {
    const absorbed = Math.min(p.shield, amount);
    p.shield -= absorbed;
    amount -= absorbed;
  }
  return amount;
}

/** Bariera odnawia się w całości, gdy przez chwilę nic nie trafiło bohatera. */
export function updateBarrier(s: GameState, dt: number): void {
  const p = s.player;
  const max = effect(s, 'barrier') * p.stats.maxHealth;
  if (max <= 0) {
    p.shield = 0;
    return;
  }
  p.shieldTimer -= dt;
  if (p.shieldTimer <= 0) p.shield = max;
  p.shield = Math.min(p.shield, max);
}
